import React, { useState } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Paper, Alert, Button } from '@mui/material';
import { Gavel, Handshake, CheckCircle, Visibility } from '@mui/icons-material';
import AgreementModal from './AgreementModal';

interface AgreementsSectionProps {
  hasProfessional: boolean;
  hasODR: boolean;
  profAgreementAccepted: boolean;
  setProfAgreementAccepted: (val: boolean) => void;
  odrAgreementAccepted: boolean;
  setOdrAgreementAccepted: (val: boolean) => void;
  declarationAccepted: boolean;
  setDeclarationAccepted: (val: boolean) => void;
  showDeclaration?: boolean;
}

const AgreementsSection: React.FC<AgreementsSectionProps> = ({
  hasProfessional,
  hasODR,
  profAgreementAccepted,
  setProfAgreementAccepted,
  odrAgreementAccepted,
  setOdrAgreementAccepted,
  declarationAccepted,
  setDeclarationAccepted,
  showDeclaration = true
}) => {
  const [profModalOpen, setProfModalOpen] = useState(false);
  const [odrModalOpen, setOdrModalOpen] = useState(false);

  if (!hasProfessional && !hasODR) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '8px' }}>
        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, mb: 1 }}>
          4. Legal & Empanelment Agreements
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please read and agree to the following agreements generated based on your selected tracks to complete your empanelment.
        </Typography>

        {hasProfessional && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 3, mb: 3 }}>
            <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2 }}>
              <Gavel sx={{ mr: 1 }} /> Professional Empanelment Agreement (Version 1.0)
            </Typography>
            {profAgreementAccepted ? (
              <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ mb: 2 }}>
                <strong>Agreement Verified:</strong> You have successfully read and accepted the Professional Empanelment Agreement.
              </Alert>
            ) : (
              <>
                <Box sx={{ height: '200px', overflowY: 'auto', p: 2, bgcolor: '#f8f9fa', border: '1px solid #ced4da', borderRadius: '4px', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>BETWEEN Kaahmuchee Solution Private Limited (KSPL) AND The Professional</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>1. Independent Professional Engagement:</strong> The Professional is empanelled and engaged solely in their capacity as an independent professional. NLP does not control, supervise or influence the professional judgment, advice, opinions, strategy, drafting or work product of the Professional.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>2. Representations and Warranties:</strong> The Professional possesses all qualifications, accreditations, competence and experience disclosed to NLP, all of which are true, complete, accurate and valid.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>3. Data Protection Laws:</strong> The Professional shall implement and maintain reasonable and appropriate technical and organisational security measures to protect Personal Data against unauthorised access, disclosure, alteration, loss or destruction.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>4. Non-Circumvention:</strong> The Professional shall not directly or indirectly solicit, divert or attempt to move Clients obtained through the Platform to engage the Professional outside the Platform in circumvention of this Agreement.</Typography>
                  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(This is a binding electronic record under the Information Technology Act, 2000)</Typography>
                  <Box sx={{ mt: 2 }}>
                     <Button 
                       variant="contained" 
                       color="primary" 
                       size="small" 
                       startIcon={<Visibility />} 
                       onClick={() => setProfModalOpen(true)}
                     >
                       View & Accept Agreement
                     </Button>
                  </Box>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={profAgreementAccepted} onChange={(e) => setProfAgreementAccepted(e.target.checked)} required disabled />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>I have read, understood, and electronically sign the Professional Empanelment Agreement.</Typography>}
                />
              </>
            )}
          </Box>
        )}

        <AgreementModal 
          open={profModalOpen} 
          onClose={() => setProfModalOpen(false)} 
          title="Professional Empanelment Agreement" 
          pdfUrl="/Assets/PDFs/NLP_Professional_Empanelment_Agreement_Combined_v1.0.pdf" 
          onAccept={() => setProfAgreementAccepted(true)} 
          isAccepted={profAgreementAccepted} 
        />

        {hasODR && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 3, mb: 3 }}>
            <Typography variant="subtitle1" color="success.main" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 2 }}>
              <Handshake sx={{ mr: 1 }} /> ODR Neutral Empanelment Agreement (Version 1.0)
            </Typography>
            {odrAgreementAccepted ? (
              <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ mb: 2 }}>
                <strong>Agreement Verified:</strong> You have successfully read and accepted the ODR Neutral Empanelment Agreement.
              </Alert>
            ) : (
              <>
                <Box sx={{ height: '200px', overflowY: 'auto', p: 2, bgcolor: '#f8f9fa', border: '1px solid #ced4da', borderRadius: '4px', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>BETWEEN Kaahmuchee Solution Private Limited (KSPL) AND The Neutral Professional</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>1. Professional Autonomy, Neutrality and Impartiality:</strong> The Neutral shall act independently and impartially in the discharge of their functions, and shall not be subject to the control or direction of the Platform with respect to the conduct of Proceedings.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>2. Duty to maintain conflict-check mechanism:</strong> The Neutral shall maintain and operate an effective conflict-of-interest checking mechanism to identify any actual, potential or perceived conflicts of interest prior to accepting an appointment.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>3. Duty of Confidentiality:</strong> The Neutral shall maintain strict and absolute confidentiality in respect of all information, communications, documents, and settlement terms exchanged during or in connection with the Proceeding.</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>4. Neutral Immunity:</strong> The Neutral shall not be liable to any party, NLP or any third person for any act, omission or decision made in good faith and in the bona fide discharge of their functions.</Typography>
                  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(This is a binding electronic record under the Information Technology Act, 2000)</Typography>
                  <Box sx={{ mt: 2 }}>
                     <Button 
                       variant="contained" 
                       color="success" 
                       size="small" 
                       startIcon={<Visibility />} 
                       onClick={() => setOdrModalOpen(true)}
                     >
                       View & Accept Agreement
                     </Button>
                  </Box>
                </Box>
                <FormControlLabel
                  control={<Checkbox checked={odrAgreementAccepted} onChange={(e) => setOdrAgreementAccepted(e.target.checked)} required disabled />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>I have read, understood, and electronically sign the ODR Neutral Empanelment Agreement.</Typography>}
                />
              </>
            )}
          </Box>
        )}

        <AgreementModal 
          open={odrModalOpen} 
          onClose={() => setOdrModalOpen(false)} 
          title="ODR Neutral Empanelment Agreement" 
          pdfUrl="/Assets/PDFs/NLP_ODR_Neutral_Empanelment_Agreement_Combined_v1.0.pdf" 
          onAccept={() => setOdrAgreementAccepted(true)} 
          isAccepted={odrAgreementAccepted} 
        />
      </Paper>

      {showDeclaration && (
        <Alert severity="warning" icon={<CheckCircle fontSize="inherit" />} sx={{ mb: 4, bgcolor: '#fff3cd', color: '#856404', borderLeft: '5px solid #ffc107', '& .MuiAlert-icon': { color: '#856404' } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>Final Form Declaration</Typography>
          <ul style={{ margin: 0, paddingLeft: '20px', marginBottom: '16px' }}>
            <li><Typography variant="body2">All information provided across this platform profile is true, complete, and accurate.</Typography></li>
            <li><Typography variant="body2">I am duly enrolled/registered with my respective statutory professional body, hold a valid Certificate of Practice (if applicable), and am legally authorized to render these services in India.</Typography></li>
            <li><Typography variant="body2">There are no ongoing, pending, or threatened disciplinary proceedings against me by any court, bar council, or regulatory institution that would impair my standing or eligibility.</Typography></li>
          </ul>
          <FormControlLabel
            control={<Checkbox checked={declarationAccepted} onChange={(e) => setDeclarationAccepted(e.target.checked)} required color="warning" />}
            label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>I Accept the Declaration and Affirm All Statements Are True.</Typography>}
          />
        </Alert>
      )}
    </Box>
  );
};

export default AgreementsSection;
